import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, ClipboardList, Pencil, AlertTriangle, Star, Copy, ChevronDown, Paperclip, FileText, X, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BottomSheet, Empty, Field, toISO, fmtData, blurSuTouch, resolveUrl } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

const GIORNI_LUNGHI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

// Da ISO a indice giorno settimana con lunedì = 0, coerente con le Impostazioni
const weekdayApp = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return (d.getDay() + 6) % 7;
};

const spostaGiorni = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return toISO(d);
};

/** Salta al giorno attivo precedente/successivo (max 30 giorni di ricerca) */
const prossimoGiornoAttivo = (iso, dir, giorniAttivi, periodoInizio = "", periodoFine = "") => {
  if (!giorniAttivi?.length) return spostaGiorni(iso, dir);
  let cur = iso;
  for (let i = 0; i < 30; i++) {
    cur = spostaGiorni(cur, dir);
    if (periodoInizio && cur < periodoInizio) continue;
    if (periodoFine && cur > periodoFine) continue;
    if (giorniAttivi.includes(weekdayApp(cur))) return cur;
  }
  return spostaGiorni(iso, dir);
};

const etichettaGiorno = (iso) => {
  const oggi = toISO(new Date());
  if (iso === oggi) return "Oggi";
  if (iso === spostaGiorni(oggi, 1)) return "Domani";
  if (iso === spostaGiorni(oggi, -1)) return "Ieri";
  return GIORNI_LUNGHI[weekdayApp(iso)];
};

/** Il testo libero diventa un elenco puntato: una riga = una voce */
function TestoProgramma({ testo }) {
  const righe = (testo || "").split("\n").map((r) => r.trim()).filter(Boolean);
  if (righe.length === 0) return null;
  if (righe.length === 1) return <p className="text-sm text-slate-700 font-semibold leading-relaxed">{righe[0]}</p>;
  return (
    <ul className="space-y-1.5">
      {righe.map((r, i) => (
        <li key={i} className="text-sm text-slate-700 font-semibold leading-relaxed flex gap-2">
          <span className="text-amber-400 shrink-0 mt-px">•</span>
          <span className="min-w-0">{r}</span>
        </li>
      ))}
    </ul>
  );
}

const urlAllegato = (a) => resolveUrl(`/api/programmi/allegati/${a.id}`);
const eImmagine = (a) => (a.tipo || "").startsWith("image/");

const pesoLeggibile = (b) => {
  if (!b) return "";
  return b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
};

/**
 * Striscia di allegati. Le immagini si aprono a schermo intero, i PDF nel
 * visore del telefono. Il pulsante di rimozione compare solo a chi può
 * modificare quel programma: gli animatori vedono e basta.
 */
function Allegati({ items, puoiModificare, onCarica, onRimuovi, caricando, testId }) {
  const fileRef = useRef(null);
  const [zoom, setZoom] = useState(null);

  if (!items?.length && !puoiModificare) return null;

  return (
    <div className="mt-3" data-testid={testId}>
      {items?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((a) => (
            <div key={a.id} className="relative group">
              {eImmagine(a) ? (
                <button type="button" onClick={() => setZoom(a)}
                  className="block h-20 w-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-50"
                  data-testid={`allegato-img-${a.id}`}>
                  <img src={urlAllegato(a)} alt={a.nome} className="h-full w-full object-cover" loading="lazy" />
                </button>
              ) : (
                <a href={urlAllegato(a)} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 h-20 rounded-xl border border-slate-200 bg-slate-50 px-3 hover:border-amber-400 transition-colors max-w-[190px]"
                  data-testid={`allegato-pdf-${a.id}`}>
                  <FileText className="h-6 w-6 text-red-500 shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-xs font-bold text-slate-700 truncate">{a.nome}</span>
                    <span className="block text-[10px] font-semibold text-slate-400">PDF · {pesoLeggibile(a.dimensione)}</span>
                  </span>
                </a>
              )}
              {puoiModificare && (
                <button type="button" onClick={() => onRimuovi(a)}
                  className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center text-slate-400 hover:text-red-500"
                  aria-label={`Rimuovi ${a.nome}`} data-testid={`allegato-rimuovi-${a.id}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {puoiModificare && (
        <>
          <input ref={fileRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onCarica(f); e.target.value = ""; }}
            data-testid={`${testId}-input`} />
          <button type="button" className="btn-ghost !py-1.5 text-xs mt-2" disabled={caricando}
            onClick={() => fileRef.current?.click()} data-testid={`${testId}-btn`}>
            {caricando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
            {caricando ? "Caricamento..." : "Allega file"}
          </button>
          {/* Un allegato del programma lo apre CHIUNQUE abbia un account, anche
              gli aiuto animatori più giovani, e il contenuto non lo controlla
              nessuno: un elenco con i cognomi dei bambini o un certificato
              medico caricato "per comodità" diventa visibile a tutti. Il modo
              più efficace di proteggerlo è non caricarlo. */}
          <p className="text-[11px] text-slate-500 font-semibold mt-1.5 leading-snug"
            data-testid={`${testId}-avvertenza`}>
            Non caricare certificati medici, documenti d'identità o dati personali non
            necessari. Il file è visibile a tutti gli animatori.
          </p>
        </>
      )}

      {/* Visore a schermo intero per le immagini */}
      {zoom && (
        <div className="fixed inset-0 z-[70] bg-slate-900/90 flex items-center justify-center p-4"
          onClick={() => setZoom(null)} data-testid="allegato-visore">
          <img src={urlAllegato(zoom)} alt={zoom.nome} className="max-h-full max-w-full rounded-2xl object-contain" />
          <button type="button" className="absolute top-[calc(1rem+env(safe-area-inset-top))] right-4 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center"
            onClick={() => setZoom(null)} aria-label="Chiudi" data-testid="allegato-visore-chiudi">
            <X className="h-5 w-5 text-slate-700" />
          </button>
        </div>
      )}
    </div>
  );
}

function QuandoAggiornato({ da, il }) {
  if (!il) return null;
  const d = new Date(il);
  const oggi = toISO(new Date());
  const giorno = toISO(d);
  const ora = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const quando =
    giorno === oggi ? `oggi alle ${ora}`
    : giorno === spostaGiorni(oggi, -1) ? `ieri alle ${ora}`
    : `il ${fmtData(giorno)} alle ${ora}`;
  return (
    <p className="text-[11px] text-slate-400 font-semibold mt-3">
      Ultima modifica{da ? ` di ${da}` : ""}, {quando}
    </p>
  );
}

export default function Programma() {
  const { user } = useAuth();
  const [data, setData] = useState(toISO(new Date()));
  const [dati, setDati] = useState(null);
  const [sheet, setSheet] = useState(null);        // { gruppo_id, gruppo_nome, testo }
  const [storico, setStorico] = useState([]);
  const [saving, setSaving] = useState(false);
  const [altriAperti, setAltriAperti] = useState(false);
  const [caricando, setCaricando] = useState(null); // gruppo_id in caricamento, o "giornata"

  const load = useCallback(() => {
    api.get(`/programmi?data=${data}`).then((r) => setDati(r.data));
  }, [data]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const giorniAttivi = dati?.giorni_attivi || [];
  const periodoInizio = dati?.periodo_inizio || "";
  const periodoFine = dati?.periodo_fine || "";
  const mioProgramma = useMemo(() => (dati?.programmi || []).find((p) => p.e_mio_gruppo), [dati]);
  const altri = useMemo(() => (dati?.programmi || []).filter((p) => !p.e_mio_gruppo), [dati]);

  const cambiaGiorno = (dir) => setData((d) => prossimoGiornoAttivo(d, dir, giorniAttivi, periodoInizio, periodoFine));

  const apriModifica = async (p) => {
    setSheet({ gruppo_id: p.gruppo_id, gruppo_nome: p.gruppo_nome, testo: p.testo || "" });
    try {
      const { data: s } = await api.get(`/programmi/storico?gruppo_id=${p.gruppo_id}`);
      setStorico(s.filter((x) => x.data !== data));
    } catch { setStorico([]); }
  };

  const salva = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/programmi", { gruppo_id: sheet.gruppo_id, data, testo: sheet.testo });
      toast.success("Programma salvato");
      setSheet(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const caricaAllegato = async (file, gruppoId) => {
    if (!dati?.giorno_attivo) {
      toast.error("Puoi caricare allegati solo nei giorni attivi dell'oratorio");
      return;
    }
    const chiave = gruppoId || "giornata";
    setCaricando(chiave);
    try {
      const form = new FormData();
      form.append("file", file);
      const qs = gruppoId ? `?data=${data}&gruppo_id=${gruppoId}` : `?data=${data}`;
      await api.post(`/programmi/allegati${qs}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allegato caricato");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setCaricando(null); }
  };

  const rimuoviAllegato = async (a) => {
    try {
      await api.delete(`/programmi/allegati/${a.id}`);
      toast.success("Allegato rimosso");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!dati) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  const CardProgramma = ({ p, principale }) => (
    <div className={`card-app ${principale ? "p-5" : "p-4"}`} data-testid={`programma-${p.gruppo_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className={`font-display font-extrabold text-slate-900 break-words ${principale ? "text-lg" : "text-base"}`}>
            {p.gruppo_nome}
          </h2>
          {p.referenti.length > 0 && (
            <p className="text-[11px] text-slate-400 font-semibold flex items-center gap-1 mt-0.5">
              <Star className="h-3 w-3 text-emerald-500 fill-current shrink-0" />
              <span className="truncate">{p.referenti.join(", ")}</span>
            </p>
          )}
        </div>
        {p.puoi_modificare && (
          <button className="btn-ghost !py-1.5 text-xs shrink-0" onClick={() => apriModifica(p)} disabled={!dati.giorno_attivo}
            data-testid={`modifica-programma-${p.gruppo_id}`}>
            <Pencil className="h-3.5 w-3.5" /> {p.compilato ? "Modifica" : "Scrivi"}
          </button>
        )}
      </div>

      {p.compilato ? (
        <>
          <TestoProgramma testo={p.testo} />
          <QuandoAggiornato da={p.aggiornato_da} il={p.aggiornato_il} />
        </>
      ) : (
        <p className="text-sm text-slate-400 font-semibold">
          {p.puoi_modificare ? "Non hai ancora scritto il programma per questo giorno." : "Programma non ancora pubblicato."}
        </p>
      )}

      <Allegati items={p.allegati} puoiModificare={p.puoi_modificare && dati.giorno_attivo}
        caricando={caricando === p.gruppo_id}
        onCarica={(f) => caricaAllegato(f, p.gruppo_id)} onRimuovi={rimuoviAllegato}
        testId={`allegati-${p.gruppo_id}`} />
    </div>
  );

  return (
    <div data-testid="programma-page">
      <div className="mb-5">
        <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Programma</h1>
        <p className="text-sm text-slate-500 font-semibold">Cosa si fa in oratorio, giorno per giorno</p>
      </div>

      {/* NAVIGATORE GIORNI (salta solo tra i giorni attivi) */}
      <div className={`card-app p-4 mb-5 ${!dati.giorno_attivo ? "border border-amber-200 bg-amber-50/40" : ""}`}>
        <div className="flex items-center gap-3">
          <button className="btn-ghost !p-2 shrink-0" onClick={() => cambiaGiorno(-1)} data-testid="giorno-prev" aria-label="Giorno precedente">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0 text-center">
            <p className="font-display font-extrabold text-slate-900 leading-tight">{etichettaGiorno(data)}</p>
            <p className="text-xs text-slate-400 font-semibold">{fmtData(data)}</p>
          </div>
          <button className="btn-ghost !p-2 shrink-0" onClick={() => cambiaGiorno(1)} data-testid="giorno-next" aria-label="Giorno successivo">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button type="button" onClick={() => setData(toISO(new Date()))}
            className="text-[11px] font-bold text-slate-500 hover:text-amber-600 rounded-full bg-slate-50 px-3 py-1"
            data-testid="vai-oggi">Oggi</button>
          <button type="button" onClick={() => setData(prossimoGiornoAttivo(toISO(new Date()), 1, giorniAttivi, periodoInizio, periodoFine))}
            className="text-[11px] font-bold text-slate-500 hover:text-amber-600 rounded-full bg-slate-50 px-3 py-1"
            data-testid="vai-prossimo">Prossimo giorno attivo</button>
          <input type="date" className="input-app !py-1.5 !px-3 text-xs ml-auto max-w-[160px]"
            value={data} onChange={(e) => e.target.value && setData(e.target.value)} data-testid="programma-data-input" />
        </div>

        {!dati.giorno_attivo && (
          <p className="text-xs font-bold text-amber-700 flex items-start gap-1.5 mt-3" data-testid="giorno-non-attivo">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
            Questo giorno non rientra nel periodo o nei giorni attivi dell'oratorio: il programma non è compilabile.
          </p>
        )}
      </div>

      {/* ALLEGATI DELLA GIORNATA: valgono per tutti i gruppi */}
      {(dati.allegati_giornata?.length > 0 || dati.puoi_allegare_giornata) && (
        <div className="card-app p-4 mb-5" data-testid="allegati-giornata-card">
          <p className="font-display font-extrabold text-slate-900 text-sm flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-sky-500" /> Materiale della giornata
          </p>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">
            {dati.allegati_giornata?.length > 0
              ? "Valido per tutti i gruppi"
              : "Nessun file: carica qui ciò che vale per tutti i gruppi (programma generale, mappe, volantini)."}
          </p>
          <Allegati items={dati.allegati_giornata} puoiModificare={dati.giorno_attivo && dati.puoi_allegare_giornata}
            caricando={caricando === "giornata"}
            onCarica={(f) => caricaAllegato(f, null)} onRimuovi={rimuoviAllegato}
            testId="allegati-giornata" />
        </div>
      )}

      {dati.programmi.length === 0 ? (
        <Empty icon={ClipboardList} text="Nessun gruppo creato"
          sub="Il programma si scrive per gruppo: creane almeno uno nella sezione Gruppi" />
      ) : (
        <>
          {/* IL MIO GRUPPO IN EVIDENZA */}
          {mioProgramma && (
            <div className="mb-5">
              <p className="label-app">Il tuo gruppo</p>
              <CardProgramma p={mioProgramma} principale />
            </div>
          )}

          {/* GLI ALTRI GRUPPI, COLLASSATI */}
          {altri.length > 0 && (
            <div>
              <button type="button" onClick={() => setAltriAperti((v) => !v)}
                className="w-full flex items-center gap-2 mb-2 text-left"
                aria-expanded={altriAperti} data-testid="altri-gruppi-toggle">
                <p className="label-app !mb-0">{mioProgramma ? "Gli altri gruppi" : "Gruppi"} ({altri.length})</p>
                <span className="text-[11px] font-bold text-slate-400">
                  {altri.filter((p) => p.compilato).length} su {altri.length} compilati
                </span>
                <ChevronDown className={`h-4 w-4 text-slate-400 ml-auto transition-transform ${altriAperti ? "rotate-180" : ""}`} />
              </button>
              {altriAperti && (
                <div className="grid md:grid-cols-2 gap-3">
                  {altri.map((p) => <CardProgramma key={p.gruppo_id} p={p} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* SHEET DI MODIFICA */}
      <BottomSheet open={!!sheet} onOpenChange={(o) => { if (!o) setSheet(null); }}
        title={sheet ? `Programma · ${sheet.gruppo_nome}` : ""}
        description={`${etichettaGiorno(data)} ${fmtData(data)} · una riga per ogni cosa da fare`}
        testId="programma-sheet">
        {sheet && (
          <form onSubmit={salva}>
            {storico.length > 0 && (
              <Field label="Copia da un altro giorno (facoltativo)" hint="Utile quando le giornate si somigliano: puoi poi modificare il testo">
                <select className="input-app" value=""
                  onChange={(e) => {
                    const s = storico.find((x) => x.data === e.target.value);
                    if (s) setSheet((v) => ({ ...v, testo: s.testo }));
                    blurSuTouch(e);
                  }}
                  data-testid="programma-copia-select">
                  <option value="">Scegli un giorno già compilato...</option>
                  {storico.map((s) => (
                    <option key={s.data} value={s.data}>
                      {fmtData(s.data)} — {s.testo.split("\n")[0].slice(0, 40)}{s.testo.length > 40 ? "…" : ""}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Cosa si fa" hint="Scrivi una cosa per riga: verranno mostrate come elenco">
              <textarea className="input-app min-h-[180px]" rows={8} autoFocus
                placeholder={"Es.\n9:00 accoglienza in cortile\n9:30 preghiera\n10:00 giochi a squadre\nPortare i gonfiabili dal magazzino"}
                value={sheet.testo}
                onChange={(e) => setSheet({ ...sheet, testo: e.target.value })}
                data-testid="programma-testo-input" />
            </Field>

            <div className="rounded-2xl bg-slate-50 p-3 mb-4">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Anteprima</p>
              {sheet.testo.trim()
                ? <TestoProgramma testo={sheet.testo} />
                : <p className="text-xs text-slate-400 font-semibold">Il programma è vuoto: salvando, verrà rimosso.</p>}
            </div>

            <button className="btn-primary w-full" disabled={saving} data-testid="programma-submit-btn">
              {saving ? "Salvataggio..." : "Salva programma"}
            </button>
          </form>
        )}
      </BottomSheet>
    </div>
  );
}
